import React, { useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import Icon from '../AppIcon';

function MediaThumb({ item, selected, onToggle }) {
  const isVideo = item.kind === 'video';
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative w-full text-left rounded-lg border-2 overflow-hidden transition-colors ${
        selected
          ? 'border-primary ring-2 ring-primary/25'
          : 'border-border opacity-75 hover:opacity-100'
      }`}
    >
      <div className="aspect-video bg-muted relative">
        {isVideo ? (
          <video
            src={item.url}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
          />
        ) : (
          <img src={item.url} alt="" className="w-full h-full object-cover" />
        )}
        <span
          className={`absolute top-1.5 left-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            isVideo
              ? 'bg-violet-900/85 text-white'
              : 'bg-black/60 text-white'
          }`}
        >
          {isVideo ? 'Vidéo' : 'Image'}
        </span>
        <span
          className={`absolute top-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-full border-2 ${
            selected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-white/90 bg-black/40 text-white'
          }`}
          aria-hidden
        >
          {selected ? <Icon name="Check" size={14} /> : null}
        </span>
      </div>
      <p className="text-[10px] text-muted-foreground px-2 py-1.5 line-clamp-2 break-all leading-snug">
        {item.url}
      </p>
    </button>
  );
}

/**
 * Validation des médias proposés par l’IA (images / vidéos).
 */
export default function HubupAiSuggestedMediaReview({
  items,
  maxSelect = 5,
  loading = false,
  error = null,
  onConfirm,
  onSkip,
  compact = false,
}) {
  const list = useMemo(
    () =>
      (Array.isArray(items) ? items : []).filter(
        (x) => x?.url && (x.kind === 'video' || x.kind === 'image'),
      ),
    [items],
  );

  const [selected, setSelected] = useState(() => new Set(list.map((_, i) => i)));

  useEffect(() => {
    setSelected(new Set(list.map((_, i) => i)));
  }, [list]);

  const toggle = (index) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
        return next;
      }
      if (next.size >= maxSelect) return prev;
      next.add(index);
      return next;
    });
  };

  const selectedUrls = list
    .filter((_, i) => selected.has(i))
    .map((x) => x.url);

  if (!list.length) return null;

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {!compact ? (
        <div>
          <p className="text-sm font-medium text-foreground">
            Médias repérés sur la page
          </p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            L’IA a sélectionné des illustrations pertinentes. Cochez celles à
            inclure dans la notification (images et vidéos uniquement, max{' '}
            {maxSelect}).
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground leading-relaxed">
          Médias proposés par l’IA — validez ceux à ajouter aux illustrations.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[min(50vh,360px)] overflow-y-auto pr-0.5">
        {list.map((item, index) => (
          <MediaThumb
            key={`${item.url}-${index}`}
            item={item}
            selected={selected.has(index)}
            onToggle={() => toggle(index)}
          />
        ))}
      </div>

      {error ? (
        <p className="text-sm text-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
        <Button
          type="button"
          variant="outline"
          disabled={loading}
          onClick={() => onSkip?.()}
        >
          {selectedUrls.length ? 'Continuer sans média' : 'Ignorer'}
        </Button>
        <Button
          type="button"
          disabled={loading}
          onClick={() => onConfirm?.(selectedUrls)}
        >
          {loading
            ? 'Import…'
            : selectedUrls.length
              ? `Ajouter ${selectedUrls.length} média${selectedUrls.length > 1 ? 's' : ''}`
              : 'Continuer'}
        </Button>
      </div>
    </div>
  );
}
